import React, { useState, useEffect } from "react";
import { useUserAuth } from "../../../context/userAuthContext";
import { db } from "../../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import {
  Star,
  Wifi,
  MessageCircle,
  Gift,
  TrendingUp,
  Camera,
  Instagram,
  AlertTriangle,
  Clock,
  CheckCircle,
  Sparkles,
  Copy,
  Check,
  Wallet,
  Calendar,
  User,
  Crown,
  ArrowLeft,
} from "lucide-react";

const MiguelPro = () => {
  const { user } = useUserAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      const docRef = doc(db, "Users", user.uid);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserInfo({
            ...userData,
            uid: user.uid,
            email: userData.email || user.email,
          });
        } else {
          setUserInfo({
            uid: user.uid,
            email: user.email,
            pro: false,
          });
        }
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [user]);

  // Generar wallet address desde UID
  const generateWalletAddress = (uid) => {
    return `0x${uid.substring(0, 8)}${uid.substring(uid.length - 8)}`;
  };

  // Formatear fecha de creación
  const formatJoinDate = (createdAt) => {
    if (!createdAt) return "08/2025";

    try {
      const date = new Date(createdAt);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${year}`;
    } catch (error) {
      return "08/2025";
    }
  };

  // Copiar wallet address
  const copyWalletAddress = async () => {
    if (userInfo?.uid) {
      const walletAddress = generateWalletAddress(userInfo.uid);
      try {
        await navigator.clipboard.writeText(walletAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Error copying wallet address:", err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#14840c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Cargando tu perfil PRO...</p>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Error al cargar la información del usuario</p>
        </div>
      </div>
    );
  }

  const isPro = userInfo.pro === true;
  const walletAddress = generateWalletAddress(userInfo.uid);
  const joinDate = formatJoinDate(userInfo.createdAt);
  const userName = `${userInfo.nombre || ""} ${userInfo.apellido || ""}`.trim() || "Usuario MiguelMX";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 lg:top-18 z-20">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
                MiguelMX PRO
              </h1>
              <p className="text-slate-600">
                {isPro ? "Tu membresía PRO está activa" : "Activa tu membresía PRO"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        {isPro ? (
          /* Usuario PRO - Mostrar tarjeta y beneficios */
          <div className="space-y-8">
            {/* Status Badge */}
            <div className="text-center">
              <div className="inline-flex items-center bg-[#14840c] text-white px-6 py-3 rounded-2xl font-semibold shadow-lg">
                <Crown className="w-5 h-5 mr-2" />
                Miembro PRO Activo
              </div>
            </div>

            {/* Tarjeta Digital PRO */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
                Tu Tarjeta Digital PRO
              </h2>

              <div className="max-w-md mx-auto mb-8">
                <div className="relative group cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                  <div className="relative bg-gradient-to-br from-[#14840c] via-green-600 to-emerald-700 p-8 rounded-3xl shadow-2xl overflow-hidden">
                    {/* Elementos decorativos de la tarjeta */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

                    <div className="relative z-10">
                      {/* Logo y NFC */}
                      <div className="flex justify-between items-start mb-8">
                        <div>
                          <h4 className="text-white text-2xl font-black">MiguelMX®</h4>
                          <p className="text-green-200 text-sm font-semibold">PRO MEMBER</p>
                        </div>
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                          <Wifi className="w-6 h-6 text-white transform rotate-90" />
                        </div>
                      </div>

                      {/* Nombre del usuario */}
                      <div className="mb-6">
                        <p className="text-green-200 text-xs font-medium mb-1">TITULAR</p>
                        <p className="text-white text-lg font-bold uppercase tracking-wide">{userName}</p>
                      </div>

                      {/* Wallet Address */}
                      <div className="mb-6">
                        <p className="text-green-200 text-xs font-medium mb-1">WALLET ADDRESS</p>
                        <div className="flex items-center gap-2">
                          <p className="text-white text-sm font-mono tracking-wider">{walletAddress}</p>
                          <button
                            onClick={copyWalletAddress}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-300" />
                            ) : (
                              <Copy className="w-4 h-4 text-white/70" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Información del titular */}
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-green-200 text-xs font-medium mb-1">MIEMBRO DESDE</p>
                          <p className="text-white font-semibold">{joinDate}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center text-white">
                            <Star className="w-4 h-4 mr-1 text-yellow-400" />
                            <span className="text-sm font-bold">PRO</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-[#14840c] text-sm font-medium flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Tarjeta activa - Cashback habilitado
                  </p>
                </div>
              </div>
            </div>

            {/* Beneficios y Stats */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Beneficios Exclusivos */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-[#14840c] rounded-xl flex items-center justify-center mr-4">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Beneficios Activos</h3>
                </div>

                <div className="space-y-4">
                  {[
                    { icon: CheckCircle, text: "Gorra oficial Black Clover MiguelMX®" },
                    { icon: CheckCircle, text: "Cashback en contrataciones" },
                    { icon: CheckCircle, text: "Consultas ilimitadas" },
                    { icon: CheckCircle, text: "Acceso a servicios premium" },
                    { icon: CheckCircle, text: "Merch y recompensas exclusivas" },
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <benefit.icon className="w-5 h-5 text-[#14840c] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Activar Cashback */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center mr-4">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Activa tu Cashback</h3>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <Camera className="w-5 h-5 text-pink-500 flex-shrink-0 mt-1" />
                    <span className="text-slate-600 font-medium">
                      Sube una historia en Instagram con tu gorra MiguelMX®
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-5 h-5 text-pink-500 flex-shrink-0 mt-1 font-bold">@</span>
                    <span className="text-slate-600 font-medium">
                      Etiqueta a @miguelmx_fin
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#14840c] flex-shrink-0 mt-1" />
                    <span className="text-slate-600 font-medium">
                      Consulta con tu asesor los modelos disponibles
                    </span>
                  </div>
                </div>

                <button className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 rounded-xl font-bold hover:from-pink-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center gap-2">
                  <Instagram className="w-5 h-5" />
                  Publicar en Instagram
                </button>
              </div>
            </div>

            {/* Stats del usuario */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Wallet, label: "Wallet ID", value: walletAddress.substring(0, 10) + "...", color: "bg-blue-500" },
                { icon: Calendar, label: "Miembro desde", value: joinDate, color: "bg-green-500" },
                { icon: Star, label: "Nivel", value: "PRO", color: "bg-yellow-500" },
                { icon: TrendingUp, label: "Cashback acumulado", value: "$0.00", color: "bg-purple-500" },
              ].map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-slate-900 font-bold text-lg">{stat.value}</p>
                  <p className="text-slate-500 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Usuario NO PRO - Mensaje de contactar asesor */
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <AlertTriangle className="w-10 h-10 text-orange-500" />
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Cuenta PRO No Activada
              </h2>

              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Para acceder a tu tarjeta digital PRO y todos los beneficios exclusivos,
                necesitas que tu asesor active tu membresía PRO.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Gorra oficial Black Clover MiguelMX®",
                  "Cashback en todas tus contrataciones",
                  "Consultas ilimitadas con tu asesor",
                  "Acceso exclusivo a servicios premium",
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center justify-center space-x-3">
                    <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-slate-600 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              <button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center justify-center gap-3 mb-6">
                <MessageCircle className="w-6 h-6" />
                Contactar a mi Asesor
              </button>

              <p className="text-slate-500 text-sm">
                Una vez activada, tu tarjeta aparecerá automáticamente aquí
              </p>
            </div>

            {/* Info del usuario actual */}
            <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-bold">{userName}</p>
                  <p className="text-slate-500 text-sm">{userInfo.email}</p>
                </div>
              </div>
              <p className="text-slate-500 text-sm text-center">
                Miembro desde: {joinDate}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiguelPro;